import { useEffect, useState } from 'react'
import { VisuallyHidden } from '@overdose/components'
import classNames from 'classnames'
import { BrazeBanner, Link } from '~/components'
import FooterBackground from '~/components/FooterBackground/FooterBackground'
import { HintTooltip } from '~/components/HintTooltip'
import { Icon } from '~/components/Icon'
import { Image } from '~/components/Image'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import { getImageSrc } from '~/helpers'
import { usePage } from '~/hooks'
import jaycarLogo from '~/public/images/jaycar-logo.svg'
import rtmLogo from '~/public/images/rtm-logo.svg'
import styles from './SiteFooter.module.scss'
import { SiteFooterProps } from './SiteFooter.types'
import { SiteFooterHoursColumn } from './SiteFooterHoursColumn'
import { SiteFooterLinkColumn } from './SiteFooterLinkColumn'

const MOBILE_MEDIA_QUERY = '(max-width: 767px)'

// Payment cards render in a 46px box (see `.paymentItem`). Request the asset
// at 1x, 2x and 3x so high-density screens don't get a blurry upscale.
const PAYMENT_CARD_WIDTH_1X = '46'
const PAYMENT_CARD_WIDTH_2X = '92'
const PAYMENT_CARD_WIDTH_3X = '138'

const getPaymentCardSrcSet = (src: string) => {
  if (!src) {
    return undefined
  }

  return [
    `${getImageSrc(src, PAYMENT_CARD_WIDTH_1X)} 1x`,
    `${getImageSrc(src, PAYMENT_CARD_WIDTH_2X)} 2x`,
    `${getImageSrc(src, PAYMENT_CARD_WIDTH_3X)} 3x`,
  ].join(', ')
}

export const SiteFooter = ({
  width = 'wide',
  footerLinkCategories,
  socialMediaPlatforms,
  callCentreHoursTitle,
  callCentreHoursPhoneNumber,
  callCentreHours,
  acceptedCardsImages,
  newsletterBrazeBannerPlacementId,
  tradeMarkText,
  termsAndConditions,
  abn,
  currencyCode,
  footerBackgroundTexture,
  backgroundImage,
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
}: SiteFooterProps) => {
  const page = usePage()
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY)

    const syncIsMobile = () => {
      setIsMobile(mediaQuery.matches)
    }

    syncIsMobile()
    mediaQuery.addEventListener('change', syncIsMobile)

    return () => {
      mediaQuery.removeEventListener('change', syncIsMobile)
    }
  }, [])

  const logoAltValue = `${page?.shortBrandName} Logo`
  const businessNumberLabel =
    page?.countryCode?.toLowerCase() === 'nz' ? 'NBN' : 'ABN'
  const zendeskEnv =
    page?.countryCode?.toLowerCase() === 'au'
      ? process.env.NEXT_PUBLIC_ZENDESK_ID_AU
      : process.env.NEXT_PUBLIC_ZENDESK_ID_NZ

  const footerStyle: React.CSSProperties = {
    backgroundImage,
    backgroundPosition,
    backgroundRepeat,
    backgroundSize,
  }

  return (
    <footer
      className={classNames(styles.footer, {
        [styles.zendeskFooter]: zendeskEnv,
      })}
      style={footerStyle}
      id='footer'>
      {footerBackgroundTexture && (
        <div className={styles.background}>
          <FooterBackground {...footerBackgroundTexture} />
        </div>
      )}
      <SectionContainer
        width={width}
        innerClassName={styles.inner}
        wrapperClassName={styles.wrapper}>
        <div className={styles.top}>
          <div className={styles.brand}>
            <ul
              className={styles.payments}
              aria-label='Payment methods we accept'>
              {acceptedCardsImages.map((card, index) => {
                return (
                  <li key={index} className={styles.paymentItem}>
                    <HintTooltip
                      message={card.altText}
                      position='top'
                      containerClassName={styles.paymentTooltip}>
                      <Image
                        src={getImageSrc(card?.src, PAYMENT_CARD_WIDTH_3X)}
                        srcSet={getPaymentCardSrcSet(card?.src)}
                        addSrcSet={false}
                        width={card.width}
                        height={card.height}
                        alt={card.altText}
                        className={styles.paymentImage}
                      />
                    </HintTooltip>
                  </li>
                )
              })}
            </ul>
            <div className={styles.social}>
              {socialMediaPlatforms.map((social, index) => {
                return (
                  <HintTooltip
                    key={index}
                    message={social.brand}
                    position='top'
                    tooltipClassName={styles.socialTooltip}>
                    <Link
                      href={social.url}
                      target='_blank'
                      className={styles.socialButton}>
                      <Icon
                        iconName={social.iconName}
                        size={26}
                        stroke={2.4}
                        color='currentColor'
                        aria-hidden='true'
                      />
                      <VisuallyHidden>{social.brand}</VisuallyHidden>
                    </Link>
                  </HintTooltip>
                )
              })}
            </div>
            <Link title='Go to home page' href='/' className={styles.logoLink}>
              <Image
                src={jaycarLogo}
                alt={logoAltValue}
                width={148}
                height={40}
                className={classNames(styles.logo, styles.jaycarLogo)}
              />
              <Image
                src={rtmLogo}
                alt={logoAltValue}
                width={166}
                height={40}
                className={classNames(styles.logo, styles.rtmLogo)}
              />
            </Link>
          </div>
          {footerLinkCategories.map((category, index) => {
            return (
              <SiteFooterLinkColumn
                key={index}
                category={category}
                index={index}
                isMobile={isMobile}
              />
            )
          })}
          <SiteFooterHoursColumn
            title={callCentreHoursTitle}
            phoneNumber={callCentreHoursPhoneNumber}
            hours={callCentreHours}
            isMobile={isMobile}
          />
          {newsletterBrazeBannerPlacementId && (
            <div className={styles.promo}>
              <BrazeBanner placementId={newsletterBrazeBannerPlacementId} />
            </div>
          )}
        </div>
        <div className={styles.bottom}>
          <Typography
            tag={TypographyTag.p}
            variant={TypographyVariant.BodyRegular}
            className={styles.legal}>
            <span>
              © {new Date().getFullYear()} {tradeMarkText}
            </span>
            <span className={styles.businessNumber}>
              {businessNumberLabel} {abn}
            </span>
          </Typography>
          <div className={styles.bottomRight}>
            <div className={styles.bottomLinks}>
              {termsAndConditions.map((term, index) => {
                return (
                  <Link
                    key={index}
                    href={term.href}
                    target={term.openInNewWindow ? '_blank' : '_self'}
                    className={styles.bottomLink}>
                    <Typography
                      tag={TypographyTag.span}
                      variant={TypographyVariant.BodyRegular}
                      className={styles.bottomLinkText}>
                      {term.title}
                    </Typography>
                  </Link>
                )
              })}
            </div>
            <Typography
              tag={TypographyTag.span}
              variant={TypographyVariant.BodyRegularBold}
              className={styles.currency}>
              {currencyCode}
            </Typography>
          </div>
        </div>
      </SectionContainer>
    </footer>
  )
}
